// Formato de magnitudes según el sistema de unidades elegido. Los valores siempre están en SI.
import { formatQuantity, unit, type Quantity, type Unit, type UnitSystem } from '@openrocket-web/physics';

export interface Fmt {
  readonly system: UnitSystem;
  /** Unidad para dimensiones del cohete (mm o in). */
  readonly dim: Unit;
  readonly dist: Unit;
  readonly mass: Unit;
  readonly speed: Unit;
  readonly accel: Unit;
  readonly pressure: Unit;
  readonly temp: Unit;
  readonly area: Unit;
  readonly angle: Unit;
  /** Texto con unidad. */
  q(value: number, quantity: Quantity, u: Unit, digits?: number): string;
  dimension(m: number, digits?: number): string;
  distance(m: number, digits?: number): string;
  massText(kg: number, digits?: number): string;
  speedText(v: number, digits?: number): string;
  accelText(a: number, digits?: number): string;
  seconds(s: number, digits?: number): string;
}

export function makeFormat(system: UnitSystem): Fmt {
  const metric = system === 'metric';
  const dim = unit(metric ? 'mm' : 'in');
  const dist = unit(metric ? 'm' : 'ft');
  const mass = unit(metric ? 'g' : 'oz');
  const speed = unit(metric ? 'm/s' : 'ft/s');
  const accel = unit(metric ? 'm/s2' : 'ft/s2');
  const pressure = unit(metric ? 'hPa' : 'inHg');
  const temp = unit(metric ? 'C' : 'F');
  const area = unit(metric ? 'cm2' : 'in2');
  const angle = unit('deg');
  const q = (value: number, quantity: Quantity, u: Unit, digits = 4): string => formatQuantity(value, quantity, system, digits, u.id);
  return {
    system, dim, dist, mass, speed, accel, pressure, temp, area, angle, q,
    dimension: (m, d) => q(m, 'length', dim, d),
    distance: (m, d) => q(m, 'length', dist, d),
    massText: (kg, d) => q(kg, 'mass', mass, d),
    speedText: (v, d) => q(v, 'speed', speed, d),
    accelText: (a, d) => q(a, 'acceleration', accel, d),
    seconds: (s, d = 4) => (Number.isFinite(s) ? `${Number(s.toPrecision(d))} s` : '— s'),
  };
}

/** Texto numérico sin ceros sobrantes, para mostrar un valor en un campo editable. */
export const plain = (v: number): string => (Number.isFinite(v) ? String(Number(v.toPrecision(7))) : '');
